import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, Palette, Bold, Italic, Underline, Strikethrough,
  Paperclip, Loader2, FileText,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { ProjectAttachment, ProjectStickyNote } from '../../types';
import { DEFAULT_STICKY_NOTE_COLOR, STICKY_NOTE_COLORS } from '../../utils/projectConstants';
import {
  getFileAttachments,
  getImageAttachments,
  removeProjectAttachmentFromStorage,
  safeProjectAttachments,
  uploadStickyNoteAttachment,
} from '../../utils/projectAttachments';

interface ProjectStickyNotesColumnProps {
  projectId: string;
  notes: ProjectStickyNote[];
  onUpdateNotes: (notes: ProjectStickyNote[]) => Promise<void>;
}

type NoteFormatKey = 'bold' | 'italic' | 'underline' | 'strikethrough';

interface NoteFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
}

const NOTE_WIDTH = 168;
const NOTE_MIN_HEIGHT = 120;
const CANVAS_WIDTH = 200;

const EMPTY_FORMAT: NoteFormat = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
};

function noteFormatFromNote(note: ProjectStickyNote): NoteFormat {
  return {
    bold: !!note.bold,
    italic: !!note.italic,
    underline: !!note.underline,
    strikethrough: !!note.strikethrough,
  };
}

function noteTextClasses(format: NoteFormat): string {
  return [
    'text-base leading-relaxed text-center text-gray-900 whitespace-pre-wrap break-words',
    format.bold && 'font-bold',
    format.italic && 'italic',
    format.underline && 'underline',
    format.strikethrough && 'line-through',
  ].filter(Boolean).join(' ');
}

function defaultNoteLayout(index: number): Pick<ProjectStickyNote, 'x' | 'y'> {
  const row = Math.floor(index / 1);
  return {
    x: 4,
    y: 12 + row * (NOTE_MIN_HEIGHT + 16),
  };
}

function resolveNoteLayout(note: ProjectStickyNote, index: number) {
  const defaults = defaultNoteLayout(index);
  return {
    x: note.x ?? defaults.x!,
    y: note.y ?? defaults.y!,
  };
}

function noteDisplayHeight(note: ProjectStickyNote): number {
  const images = getImageAttachments(note.attachments).length;
  const files = getFileAttachments(note.attachments).length;
  const textLines = note.text?.trim() ? 1 : 0;
  return NOTE_MIN_HEIGHT + images * 72 + files * 18 + (textLines ? 0 : 0);
}

function canvasHeight(notes: ProjectStickyNote[]): number {
  if (notes.length === 0) return 280;
  let maxBottom = 280;
  notes.forEach((note, i) => {
    const { y } = resolveNoteLayout(note, i);
    maxBottom = Math.max(maxBottom, y + noteDisplayHeight(note) + 48);
  });
  return maxBottom;
}

interface StickyNoteContentProps {
  note: ProjectStickyNote;
  format: NoteFormat;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (value: string) => void;
  onStartEdit: () => void;
  onCommitEdit: () => void;
  onCancelEdit: () => void;
  onRemoveAttachment: (attachment: ProjectAttachment) => void;
  onPasteFiles?: (files: File[]) => void;
}

const StickyNoteContent: React.FC<StickyNoteContentProps> = ({
  note,
  format,
  isEditing,
  editText,
  onEditTextChange,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onRemoveAttachment,
  onPasteFiles,
}) => {
  const imageAttachments = getImageAttachments(note.attachments);
  const fileAttachments = getFileAttachments(note.attachments);
  const hasContent = !!note.text?.trim() || imageAttachments.length > 0 || fileAttachments.length > 0;

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageFiles = items
      .filter(i => i.kind === 'file' && i.type.startsWith('image/'))
      .map(i => i.getAsFile())
      .filter((f): f is File => !!f);
    if (imageFiles.length === 0) return;
    e.preventDefault();
    onPasteFiles?.(imageFiles);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-2 pb-3 pt-0.5 min-h-[72px] w-full gap-1.5">
      {imageAttachments.length > 0 && (
        <div className="w-full space-y-1">
          {imageAttachments.map(a => (
            <div key={a.url} className="group/img relative w-full">
              <a href={a.url} target="_blank" rel="noreferrer" onPointerDown={e => e.stopPropagation()}>
                <img
                  src={a.url}
                  alt={a.name || 'Imagen'}
                  className="w-full max-h-28 object-contain rounded-sm"
                  draggable={false}
                />
              </a>
              <button
                type="button"
                title="Quitar imagen"
                onPointerDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  onRemoveAttachment(a);
                }}
                className="absolute top-0.5 right-0.5 p-0.5 rounded-sm bg-black/50 text-white opacity-0 group-hover/img:opacity-100 transition-opacity"
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {fileAttachments.length > 0 && (
        <div className="w-full space-y-0.5">
          {fileAttachments.map(a => (
            <div
              key={a.url}
              className="group/file flex items-center gap-1 w-full min-w-0"
              onPointerDown={e => e.stopPropagation()}
            >
              <FileText className="w-3 h-3 text-gray-700 flex-shrink-0" />
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="flex-1 min-w-0 text-[10px] text-gray-800 hover:underline truncate text-center"
              >
                {a.name || 'Archivo'}
              </a>
              <button
                type="button"
                title="Quitar archivo"
                onClick={e => {
                  e.stopPropagation();
                  onRemoveAttachment(a);
                }}
                className="p-0.5 rounded-sm text-gray-600 hover:text-red-700 opacity-0 group-hover/file:opacity-100 transition-opacity flex-shrink-0"
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {isEditing ? (
        <textarea
          autoFocus
          rows={3}
          value={editText}
          onChange={e => onEditTextChange(e.target.value)}
          onBlur={onCommitEdit}
          onPointerDown={e => e.stopPropagation()}
          onPaste={handlePaste}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onCommitEdit();
            }
            if (e.key === 'Escape') onCancelEdit();
          }}
          className={`w-full bg-transparent resize-none outline-none placeholder:text-gray-600/70 ${noteTextClasses(format)}`}
          style={{ fontSize: '1rem' }}
          placeholder={hasContent ? 'Texto...' : 'Escribí acá...'}
        />
      ) : (
        <button
          type="button"
          onPointerDown={e => e.stopPropagation()}
          onClick={onStartEdit}
          className={`w-full ${note.text?.trim() ? `min-h-[1.5rem] ${noteTextClasses(format)}` : 'text-sm text-gray-600/70'}`}
          style={{ fontSize: note.text?.trim() ? '1rem' : undefined }}
        >
          {note.text?.trim() || (hasContent ? 'Click para editar texto...' : 'Click para escribir...')}
        </button>
      )}
    </div>
  );
};

interface FormatToolbarProps {
  format: NoteFormat;
  onToggle: (key: NoteFormatKey) => void;
  visible?: boolean;
}

const FormatToolbar: React.FC<FormatToolbarProps> = ({ format, onToggle, visible = true }) => (
  <div
    className={`flex items-center gap-0.5 px-1.5 py-0.5 transition-opacity ${
      visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    onPointerDown={e => e.stopPropagation()}
  >
    {([
      ['bold', Bold, 'Negrita'],
      ['italic', Italic, 'Cursiva'],
      ['underline', Underline, 'Subrayado'],
      ['strikethrough', Strikethrough, 'Tachado'],
    ] as const).map(([key, Icon, title]) => (
      <button
        key={key}
        type="button"
        title={title}
        onClick={e => {
          e.stopPropagation();
          onToggle(key);
        }}
        className={`p-0.5 rounded-sm transition-colors ${
          format[key]
            ? 'bg-black/15 text-gray-900'
            : 'bg-black/5 hover:bg-black/10 text-gray-700'
        }`}
      >
        <Icon className="w-3 h-3" />
      </button>
    ))}
  </div>
);

const ProjectStickyNotesColumn: React.FC<ProjectStickyNotesColumnProps> = ({
  projectId,
  notes,
  onUpdateNotes,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editFormat, setEditFormat] = useState<NoteFormat>(EMPTY_FORMAT);
  const [colorPickerId, setColorPickerId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [uploadingNoteId, setUploadingNoteId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetNoteId, setUploadTargetNoteId] = useState<string | null>(null);
  const dragRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const saveNotes = async (next: ProjectStickyNote[]) => {
    await onUpdateNotes(next);
  };

  const startEdit = (note: ProjectStickyNote) => {
    setEditingId(note.id);
    setEditText(note.text);
    setEditFormat(noteFormatFromNote(note));
    setColorPickerId(null);
  };

  const addNoteAttachments = async (noteId: string, files: File[]) => {
    if (files.length === 0) return;
    const note = notes.find(n => n.id === noteId);
    if (!note) return;

    setUploadingNoteId(noteId);
    try {
      const existing = safeProjectAttachments(note.attachments);
      const uploaded: ProjectAttachment[] = [];
      for (const file of files) {
        uploaded.push(await uploadStickyNoteAttachment(projectId, noteId, file));
      }
      const now = new Date().toISOString();
      await saveNotes(
        notes.map(n =>
          n.id === noteId
            ? { ...n, attachments: [...existing, ...uploaded], updated_at: now }
            : n
        )
      );
    } finally {
      setUploadingNoteId(null);
    }
  };

  const removeNoteAttachment = async (noteId: string, attachment: ProjectAttachment) => {
    const note = notes.find(n => n.id === noteId);
    if (!note) return;
    const next = safeProjectAttachments(note.attachments).filter(a => a.url !== attachment.url);
    await removeProjectAttachmentFromStorage(attachment.path);
    const now = new Date().toISOString();
    await saveNotes(
      notes.map(n =>
        n.id === noteId ? { ...n, attachments: next, updated_at: now } : n
      )
    );
  };

  const commitEdit = async (id: string) => {
    const text = editText.trim();
    const note = notes.find(n => n.id === id);
    const attachments = safeProjectAttachments(note?.attachments);
    if (!text && attachments.length === 0) {
      for (const a of attachments) await removeProjectAttachmentFromStorage(a.path);
      await saveNotes(notes.filter(n => n.id !== id));
    } else {
      const now = new Date().toISOString();
      await saveNotes(
        notes.map(n =>
          n.id === id
            ? {
                ...n,
                text,
                bold: editFormat.bold,
                italic: editFormat.italic,
                underline: editFormat.underline,
                strikethrough: editFormat.strikethrough,
                updated_at: now,
              }
            : n
        )
      );
    }
    setEditingId(null);
    setEditText('');
    setEditFormat(EMPTY_FORMAT);
  };

  const toggleNoteFormat = async (note: ProjectStickyNote, key: NoteFormatKey) => {
    const now = new Date().toISOString();
    await saveNotes(
      notes.map(n =>
        n.id === note.id ? { ...n, [key]: !n[key], updated_at: now } : n
      )
    );
  };

  const changeColor = async (id: string, color: string) => {
    const now = new Date().toISOString();
    await saveNotes(
      notes.map(n =>
        n.id === id ? { ...n, color, updated_at: now } : n
      )
    );
    setColorPickerId(null);
  };

  const deleteNote = async (id: string) => {
    const note = notes.find(n => n.id === id);
    for (const a of safeProjectAttachments(note?.attachments)) {
      await removeProjectAttachmentFromStorage(a.path);
    }
    await saveNotes(notes.filter(n => n.id !== id));
    if (editingId === id) setEditingId(null);
    if (colorPickerId === id) setColorPickerId(null);
  };

  const triggerUpload = (noteId: string) => {
    setUploadTargetNoteId(noteId);
    fileInputRef.current?.click();
  };

  const addNote = async () => {
    const text = newText.trim();
    if (!text) return;
    const now = new Date().toISOString();
    const layout = defaultNoteLayout(notes.length);
    const note: ProjectStickyNote = {
      id: uuidv4(),
      text,
      color: STICKY_NOTE_COLORS[notes.length % STICKY_NOTE_COLORS.length],
      x: layout.x,
      y: layout.y,
      created_at: now,
      updated_at: now,
    };
    await saveNotes([...notes, note]);
    setNewText('');
    setAdding(false);
  };

  const updateNotePosition = useCallback(async (id: string, x: number, y: number) => {
    const clampedX = Math.max(0, Math.min(x, CANVAS_WIDTH - NOTE_WIDTH));
    const clampedY = Math.max(0, y);
    const now = new Date().toISOString();
    await onUpdateNotes(
      notes.map(n =>
        n.id === id ? { ...n, x: clampedX, y: clampedY, updated_at: now } : n
      )
    );
  }, [notes, onUpdateNotes]);

  const handlePointerDown = (e: React.PointerEvent, note: ProjectStickyNote, index: number) => {
    if (editingId === note.id) return;
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
    e.preventDefault();
    const layout = resolveNoteLayout(note, index);
    dragRef.current = {
      id: note.id,
      startX: e.clientX,
      startY: e.clientY,
      originX: layout.x,
      originY: layout.y,
    };
    setDraggingId(note.id);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || !canvasRef.current) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const el = canvasRef.current.querySelector(`[data-note-id="${drag.id}"]`) as HTMLElement | null;
      if (el) {
        const x = Math.max(0, Math.min(drag.originX + dx, CANVAS_WIDTH - NOTE_WIDTH));
        const y = Math.max(0, drag.originY + dy);
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
      }
    };

    const onUp = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        const x = Math.max(0, Math.min(drag.originX + dx, CANVAS_WIDTH - NOTE_WIDTH));
        const y = Math.max(0, drag.originY + dy);
        updateNotePosition(drag.id, x, y);
      }
      dragRef.current = null;
      setDraggingId(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [updateNotePosition]);

  const height = canvasHeight(notes);

  return (
    <div
      className="flex-shrink-0 relative self-stretch flex flex-col items-start"
      style={{ width: CANVAS_WIDTH, minHeight: height }}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={async e => {
          const noteId = uploadTargetNoteId;
          const files = Array.from(e.target.files || []);
          e.currentTarget.value = '';
          setUploadTargetNoteId(null);
          if (noteId && files.length > 0) await addNoteAttachments(noteId, files);
        }}
      />

      <div
        ref={canvasRef}
        className="relative w-full flex-1"
        style={{ minHeight: Math.max(height - 44, 120) }}
      >
        {notes.map((note, index) => {
          const layout = resolveNoteLayout(note, index);
          const isDragging = draggingId === note.id;
          const isEditing = editingId === note.id;
          const isUploading = uploadingNoteId === note.id;
          const displayFormat = isEditing ? editFormat : noteFormatFromNote(note);

          return (
            <div
              key={note.id}
              data-note-id={note.id}
              className={`absolute group/note select-none touch-none ${
                isDragging ? 'z-30' : 'z-10'
              }`}
              style={{
                left: layout.x,
                top: layout.y,
                width: NOTE_WIDTH,
                minHeight: noteDisplayHeight(note),
              }}
            >
              <div
                className={`relative h-full flex flex-col rounded-sm transition-shadow duration-200 ${
                  isDragging
                    ? 'shadow-[2px_6px_14px_rgba(0,0,0,0.14)]'
                    : 'shadow-[1px_3px_8px_rgba(0,0,0,0.12)] hover:shadow-[2px_5px_12px_rgba(0,0,0,0.14)]'
                }`}
                style={{ backgroundColor: note.color }}
                data-no-drag
                onDragOver={e => e.preventDefault()}
                onDrop={async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  await addNoteAttachments(note.id, Array.from(e.dataTransfer.files || []));
                }}
              >
                <div
                  className={`h-3 flex-shrink-0 border-b border-black/[0.04] ${
                    isDragging ? 'cursor-grabbing' : 'cursor-grab'
                  }`}
                  style={{
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0.35), transparent)',
                  }}
                  onPointerDown={e => handlePointerDown(e, note, index)}
                />

                <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover/note:opacity-100 transition-opacity z-10" data-no-drag>
                  <button
                    type="button"
                    title="Adjuntar archivo"
                    disabled={isUploading}
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      triggerUpload(note.id);
                    }}
                    className="p-0.5 rounded-sm bg-black/10 hover:bg-black/20 text-gray-800 disabled:opacity-50"
                  >
                    {isUploading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Paperclip className="w-3 h-3" />
                    )}
                  </button>
                  <button
                    type="button"
                    title="Color"
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      setColorPickerId(colorPickerId === note.id ? null : note.id);
                    }}
                    className="p-0.5 rounded-sm bg-black/10 hover:bg-black/20 text-gray-800"
                  >
                    <Palette className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    title="Eliminar"
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => {
                      e.stopPropagation();
                      deleteNote(note.id);
                    }}
                    className="p-0.5 rounded-sm bg-black/10 hover:bg-red-500/30 text-gray-800 hover:text-red-800"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>

                {colorPickerId === note.id && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setColorPickerId(null)} />
                    <div
                      className="absolute right-0 top-8 z-30 p-2 rounded-md border border-gray-200 bg-white dark:bg-gray-900 shadow-lg"
                      onPointerDown={e => e.stopPropagation()}
                    >
                      <div className="flex flex-wrap gap-1.5 w-[120px]">
                        {STICKY_NOTE_COLORS.map(c => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => changeColor(note.id, c)}
                            className={`w-5 h-5 rounded-sm border-2 transition-transform hover:scale-110 ${
                              note.color === c ? 'border-gray-800 dark:border-white' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {isEditing ? (
                  <FormatToolbar
                    format={editFormat}
                    onToggle={key => setEditFormat(prev => ({ ...prev, [key]: !prev[key] }))}
                  />
                ) : (
                  <div className="opacity-0 group-hover/note:opacity-100 transition-opacity">
                    <FormatToolbar
                      format={displayFormat}
                      onToggle={key => toggleNoteFormat(note, key)}
                    />
                  </div>
                )}

                <StickyNoteContent
                  note={note}
                  format={isEditing ? editFormat : displayFormat}
                  isEditing={isEditing}
                  editText={editText}
                  onEditTextChange={setEditText}
                  onStartEdit={() => startEdit(note)}
                  onCommitEdit={() => commitEdit(note.id)}
                  onCancelEdit={() => {
                    setEditingId(null);
                    setEditText('');
                    setEditFormat(EMPTY_FORMAT);
                  }}
                  onRemoveAttachment={a => removeNoteAttachment(note.id, a)}
                  onPasteFiles={files => addNoteAttachments(note.id, files)}
                />
              </div>
            </div>
          );
        })}

        {adding && (
          <div
            className="absolute z-20"
            style={{
              left: defaultNoteLayout(notes.length).x,
              top: defaultNoteLayout(notes.length).y,
              width: NOTE_WIDTH,
            }}
          >
            <div
              className="rounded-sm shadow-[1px_3px_8px_rgba(0,0,0,0.12)]"
              style={{ backgroundColor: DEFAULT_STICKY_NOTE_COLOR }}
            >
              <div
                className="h-3 border-b border-black/[0.04]"
                style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.35), transparent)' }}
              />
              <div className="flex items-center justify-center min-h-[72px] px-2.5 py-2 pb-3">
                <textarea
                  autoFocus
                  rows={4}
                  value={newText}
                  onChange={e => setNewText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      addNote();
                    }
                    if (e.key === 'Escape') {
                      setAdding(false);
                      setNewText('');
                    }
                  }}
                  placeholder="Escribí una nota..."
                  className="w-full leading-relaxed text-center bg-transparent resize-none outline-none text-gray-900 placeholder:text-gray-600/70"
                  style={{ fontSize: '1rem' }}
                />
              </div>
              <div className="flex gap-1 px-2 pb-2">
                <button
                  type="button"
                  onClick={addNote}
                  className="text-xs px-2 py-0.5 rounded bg-gray-900/75 text-white hover:bg-gray-900"
                >
                  Agregar
                </button>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setNewText(''); }}
                  className="text-xs px-2 py-0.5 rounded text-gray-800 hover:bg-black/10"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {!adding && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          title="Agregar nota"
          className="mt-2 w-9 h-9 flex items-center justify-center rounded-full bg-amber-200/90 hover:bg-amber-300 text-amber-900 shadow-md hover:shadow-lg transition-all hover:scale-105"
        >
          <Plus className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default ProjectStickyNotesColumn;
